import { MouseEvent, useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  Button,
  Flex,
  FlexColumn,
  GenerateAIButton,
  LoadingButton,
  TextField,
} from '@elementary/common-ui/components';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { Popover, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import * as api from 'shared/services/api';
import { GenerateCustomSqlApi, GenerateCustomSqlResult } from 'shared/types';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import { GeneratedCustomQuery } from './types';

const HELPER_TEXT =
  'Describe, in plain English, the rows that should fail the test. Elementary AI translates it into a SQL query and inserts it into the editor.';
const PLACEHOLDER = 'e.g. rows in daily_revenue where platform is null';
const DEFAULT_ERROR_MESSAGE =
  "Elementary AI couldn't generate a query. Please try again.";
const MAX_DESCRIPTION_LENGTH = 2000;

const PopoverStyled = styled(Popover)`
  .MuiPaper-root {
    width: 480px;
    margin-top: ${space('sm')};
    padding: ${space('xl')};
    border-radius: ${radius('lg')};
    border: 1px solid ${({ theme }) => theme.palette.border.subtle};
  }
`;

const getErrorMessage = (error: MutationError) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : DEFAULT_ERROR_MESSAGE;
};

type Props = {
  onGenerated: (generated: GeneratedCustomQuery) => void;
};

const GenerateCustomSqlPopupButton = ({ onGenerated }: Props) => {
  const selectedEnvId = useSelectedEnv();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [description, setDescription] = useState('');

  const { mutate, isLoading, data, error, reset } = useMutation<
    GenerateCustomSqlResult,
    MutationError,
    GenerateCustomSqlApi
  >((args) => api.generateCustomSql(args, selectedEnvId!));

  const handleClose = () => {
    setAnchorEl(null);
    reset();
  };

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleGenerate = () => {
    mutate(
      { description: description.trim() },
      {
        onSuccess: (res) => {
          if (!res.sql) return;
          onGenerated({
            sql: res.sql,
            description: res.description ?? undefined,
            qualityDimension: res.quality_dimension ?? undefined,
          });
          setDescription('');
          handleClose();
        },
      },
    );
  };

  const errorMessage = error
    ? getErrorMessage(error)
    : data?.error_message || undefined;

  return (
    <>
      <GenerateAIButton size="medium" onClick={handleOpen}>
        Generate
      </GenerateAIButton>
      <PopoverStyled
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <FlexColumn sx={{ gap: 2 }}>
          <FlexColumn sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: fontWeight('semibold') }}>
              Describe your test
            </Typography>
            <Typography
              sx={{ fontSize: fontSize('base'), color: 'content.secondary' }}
            >
              {HELPER_TEXT}
            </Typography>
          </FlexColumn>
          <TextField
            autoFocus
            multiline
            minRows={4}
            fullWidth
            placeholder={PLACEHOLDER}
            value={description}
            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
            onChange={(event) => setDescription(event.target.value)}
          />
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Flex sx={{ gap: 1, justifyContent: 'flex-end' }}>
            <Button color="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              loading={isLoading}
              disabled={!description.trim()}
              onClick={handleGenerate}
            >
              Generate & insert
            </LoadingButton>
          </Flex>
        </FlexColumn>
      </PopoverStyled>
    </>
  );
};

export default GenerateCustomSqlPopupButton;
